import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActionArea,
  Paper,
  Typography,
} from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';

type Responsive<T> = T | { xs?: T; sm?: T; md?: T };

export interface ModuleCardLayout {
  gridSize: { xs: number; sm: number; md: number };
  padding: Responsive<number>;
  gap: Responsive<number>;
  iconSize: Responsive<number>;
  titleFontSize?: Responsive<string>;
  descriptionFontSize?: Responsive<string>;
}

export interface ModuleCard {
  id: string;
  title: string;
  description: string;
  icon: SvgIconComponent;
  path: string;
  layout: ModuleCardLayout;
}

interface ModuleGridProps {
  modules: ModuleCard[];
}

export function ModuleGrid({ modules }: ModuleGridProps) {
  const navigate = useNavigate();

  return (
    <Grid container spacing={{ xs: 2, sm: 3 }}>
      {modules.map(({ id, title, description, icon: Icon, path, layout }) => (
        <Grid
          size={layout.gridSize}
          key={id}
        >
          <Card
            component={Paper}
            elevation={2}
            sx={{
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <CardActionArea
              onClick={() => navigate(path)}
              sx={{
                height: '100%',
                p: layout.padding,
              }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: layout.gap,
                }}
              >
                <Icon
                  sx={{
                    fontSize: layout.iconSize,
                    color: 'primary.main',
                  }}
                />
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontSize: layout.titleFontSize,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: layout.descriptionFontSize }}
                >
                  {description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
